import React, { useState, useEffect } from 'react';
import { useGameLounge } from '../context/GameLoungeContext';
import SafeIcon from '../common/SafeIcon';
import * as FiIcons from 'react-icons/fi';
import { FaPlaystation } from 'react-icons/fa';

const { FiPlay, FiStop, FiMonitor, FiClock, FiUser, FiSmartphone, FiShoppingBag, FiPackage, FiPause, FiCheckCircle, FiRefreshCw, FiPlus, FiMinus } = FiIcons;

function StationCard({ station, onOpenKiosk, roomName }) {
  const { state, dispatch } = useGameLounge();
  const [customerName, setCustomerName] = useState('');
  const [controllerCount, setControllerCount] = useState(station.controllerCount || 1);
  const [showStartForm, setShowStartForm] = useState(false);
  const [elapsedTime, setElapsedTime] = useState('00:00:00');
  const [pausedTime, setPausedTime] = useState('00:00:00');

  useEffect(() => {
    if (station.status === 'occupied' && station.startTime) {
      const interval = setInterval(() => {
        const now = new Date();
        const startTime = new Date(station.startTime);
        
        let currentPausedTime = station.totalPausedTime;
        if (station.isPaused && station.pausedAt) {
          currentPausedTime += now - new Date(station.pausedAt);
        }
        
        const activeTime = now - startTime - currentPausedTime;
        const hours = Math.floor(activeTime / (1000 * 60 * 60));
        const minutes = Math.floor((activeTime % (1000 * 60 * 60)) / (1000 * 60));
        const seconds = Math.floor((activeTime % (1000 * 60)) / 1000);
        
        setElapsedTime(
          `${hours.toString().padStart(2, '0')}:${minutes.toString().padStart(2, '0')}:${seconds.toString().padStart(2, '0')}`
        );
        
        const pauseHours = Math.floor(currentPausedTime / (1000 * 60 * 60));
        const pauseMinutes = Math.floor((currentPausedTime % (1000 * 60 * 60)) / (1000 * 60));
        const pauseSeconds = Math.floor((currentPausedTime % (1000 * 60)) / 1000);
        
        setPausedTime(
          `${pauseHours.toString().padStart(2, '0')}:${pauseMinutes.toString().padStart(2, '0')}:${pauseSeconds.toString().padStart(2, '0')}`
        );
      }, 1000);

      return () => clearInterval(interval);
    }
  }, [station.status, station.startTime, station.isPaused, station.pausedAt, station.totalPausedTime]);

  const handleStartSession = () => {
    // Allow empty customer name - will be stored as empty string
    dispatch({
      type: 'START_SESSION',
      payload: {
        stationId: station.id,
        customer: customerName.trim(),
        controllerCount: station.type === 'playstation' ? controllerCount : 1
      }
    });
    setCustomerName('');
    setControllerCount(1);
    setShowStartForm(false);
  };

  const handlePauseSession = () => {
    dispatch({
      type: 'PAUSE_SESSION',
      payload: { stationId: station.id }
    });
  };

  const handleResumeSession = () => {
    dispatch({
      type: 'RESUME_SESSION',
      payload: { stationId: station.id }
    });
  };

  const handleStopSession = () => {
    dispatch({
      type: 'STOP_SESSION',
      payload: { stationId: station.id }
    });
  };

  const handleResetStation = () => {
    dispatch({
      type: 'RESET_STATION',
      payload: { stationId: station.id }
    });
  };

  const getCompletedHours = () => {
    if (!station.startTime) return 1;
    const now = new Date();
    const startTime = new Date(station.startTime);
    
    let currentPausedTime = station.totalPausedTime;
    if (station.isPaused && station.pausedAt) {
      currentPausedTime += now - new Date(station.pausedAt);
    }
    
    const activeTime = now - startTime - currentPausedTime;
    const hours = Math.floor(activeTime / (1000 * 60 * 60));
    return hours + 1;
  };

  const formatPlayTime = (milliseconds) => {
    const hours = Math.floor(milliseconds / (1000 * 60 * 60));
    const minutes = Math.floor((milliseconds % (1000 * 60 * 60)) / (1000 * 60));
    const seconds = Math.floor((milliseconds % (1000 * 60)) / 1000);
    return `${hours.toString().padStart(2, '0')}:${minutes.toString().padStart(2, '0')}:${seconds.toString().padStart(2, '0')}`;
  };

  const formatPausedTime = (milliseconds) => {
    const hours = Math.floor(milliseconds / (1000 * 60 * 60));
    const minutes = Math.floor((milliseconds % (1000 * 60 * 60)) / (1000 * 60));
    const seconds = Math.floor((milliseconds % (1000 * 60)) / 1000);
    return `${hours.toString().padStart(2, '0')}:${minutes.toString().padStart(2, '0')}:${seconds.toString().padStart(2, '0')}`;
  };

  const getMobileOrderUrl = () => {
    const baseUrl = window.location.origin + window.location.pathname;
    return `${baseUrl}#/mobile/${station.id}`;
  };

  const copyMobileUrl = () => {
    navigator.clipboard.writeText(getMobileOrderUrl());
    alert('Mobile Bestelllink kopiert!');
  };

  const stationOrders = state.orders.filter(
    order => order.stationId === station.id && order.status === 'pending'
  );

  const handleOpenKioskClick = () => {
    const stationWithFullData = {
      ...station,
      roomName: roomName
    };
    onOpenKiosk(stationWithFullData);
  };

  const StationIcon = station.type === 'pc' ? FiMonitor : FaPlaystation;
  const iconColorClass = station.type === 'pc' ? 'text-blue-500' : 'text-blue-700';

  return (
    <div className={`bg-white rounded-lg shadow-md p-4 border-l-4 ${
      station.status === 'completed'
        ? 'border-blue-500'
        : station.status === 'occupied' 
          ? station.isPaused 
            ? 'border-yellow-500' 
            : 'border-green-500' 
          : 'border-gray-300'
    }`}>
      <div className="flex items-center justify-between mb-3">
        <div className="flex items-center space-x-2">
          <SafeIcon icon={StationIcon} className={`text-lg ${iconColorClass}`} />
          <div>
            <h3 className="font-semibold text-gray-900">{station.name}</h3>
            <p className="text-xs text-gray-500">{roomName}</p>
          </div>
        </div>
        <div className="flex flex-col items-end space-y-1">
          <div className={`px-2 py-1 rounded-full text-xs font-medium ${
            station.status === 'completed'
              ? 'bg-blue-100 text-blue-800'
              : station.status === 'occupied' 
                ? station.isPaused
                  ? 'bg-yellow-100 text-yellow-800'
                  : 'bg-green-100 text-green-800' 
                : 'bg-gray-100 text-gray-800'
          }`}>
            {station.status === 'completed'
              ? 'Abgeschlossen'
              : station.status === 'occupied' 
                ? station.isPaused 
                  ? 'Pausiert' 
                  : 'Belegt' 
                : 'Verfügbar'}
          </div>
          {stationOrders.length > 0 && (
            <div className="flex items-center space-x-1 bg-orange-100 text-orange-800 px-2 py-1 rounded-full text-xs font-medium">
              <SafeIcon icon={FiPackage} className="text-xs" />
              <span>{stationOrders.length}</span>
            </div>
          )}
        </div>
      </div>

      {station.status === 'completed' ? (
        <div className="space-y-3">
          <div className="flex items-center space-x-2 text-sm text-gray-600">
            <SafeIcon icon={FiUser} className="text-sm" />
            <span>{station.customer || 'Gast'}</span>
            {station.type === 'playstation' && station.controllerCount > 1 && (
              <span className="ml-auto px-2 py-0.5 bg-blue-100 text-blue-800 rounded-full text-xs font-semibold">
                {station.controllerCount} Controller
              </span>
            )}
          </div>
          
          <div className="bg-blue-50 border border-blue-200 rounded-md p-3">
            <div className="flex items-center space-x-2 mb-2">
              <SafeIcon icon={FiCheckCircle} className="text-blue-600" />
              <span className="font-semibold text-sm text-gray-900">Session beendet</span>
            </div>
            
            <div className="space-y-2 text-sm">
              <div className="flex justify-between">
                <span className="text-gray-600">Spielzeit:</span>
                <span className="font-medium">{formatPlayTime(station.playTime)}</span>
              </div>
              
              {station.totalPausedTime > 0 && (
                <div className="flex justify-between text-xs">
                  <span className="text-gray-500">Pausiert:</span>
                  <span className="text-gray-500">{formatPausedTime(station.totalPausedTime)}</span>
                </div>
              )}
              
              <div className="flex justify-between pt-2 border-t border-blue-200">
                <span className="text-gray-600">Gesamtbetrag:</span>
                <span className="font-bold text-lg text-green-600">
                  {station.finalAmount.toFixed(2)} {state.settings.currency}
                </span>
              </div>
            </div>
          </div>

          <button
            onClick={handleResetStation}
            className="w-full bg-blue-500 hover:bg-blue-600 text-white px-4 py-2 rounded-md font-medium transition-colors flex items-center justify-center space-x-2"
          >
            <SafeIcon icon={FiRefreshCw} className="text-sm" />
            <span>Zurücksetzen</span>
          </button>
        </div>
      ) : station.status === 'occupied' ? (
        <div className="space-y-3">
          <div className="flex items-center space-x-2 text-sm text-gray-600">
            <SafeIcon icon={FiUser} className="text-sm" />
            <span>{station.customer || 'Gast'}</span>
            {station.type === 'playstation' && station.controllerCount > 1 && (
              <span className="ml-auto px-2 py-0.5 bg-blue-100 text-blue-800 rounded-full text-xs font-semibold">
                {station.controllerCount} Controller
              </span>
            )}
          </div>
          <div className="space-y-1">
            <div className="flex items-center space-x-2 text-sm text-gray-600">
              <SafeIcon icon={FiClock} className="text-sm" />
              <span className="font-medium">Spielzeit: {elapsedTime}</span>
            </div>
            {station.totalPausedTime > 0 && (
              <div className="flex items-center space-x-2 text-xs text-gray-500 ml-5">
                <SafeIcon icon={FiPause} className="text-xs" />
                <span>Pausiert: {pausedTime}</span>
              </div>
            )}
          </div>
          <div className="bg-blue-50 border border-blue-200 rounded-md p-2">
            <div className="text-xs text-gray-600 mb-1">
              Abgerechnete Stunden: {getCompletedHours()}
            </div>
            <div className="text-lg font-bold text-green-600">
              {station.currentAmount.toFixed(2)} {state.settings.currency}
            </div>
          </div>

          {stationOrders.length > 0 && (
            <div className="bg-orange-50 border border-orange-200 rounded-md p-3">
              <div className="flex items-center space-x-2 mb-2">
                <SafeIcon icon={FiPackage} className="text-orange-600" />
                <span className="font-medium text-sm text-gray-900">
                  Offene Bestellungen ({stationOrders.length})
                </span>
              </div>
              <div className="space-y-1 max-h-24 overflow-y-auto">
                {stationOrders.map(order => (
                  <div key={order.id} className="text-xs text-gray-600 flex justify-between">
                    <span>{order.items.length} Artikel</span>
                    <span className="font-medium">{order.total.toFixed(2)} {state.settings.currency}</span>
                  </div>
                ))}
              </div>
            </div>
          )}

          <div className="grid grid-cols-2 gap-2">
            <button
              onClick={handleStopSession}
              className="bg-red-500 hover:bg-red-600 text-white px-3 py-2 rounded-md text-sm font-medium transition-colors flex items-center justify-center space-x-1"
            >
              <SafeIcon icon={FiStop} className="text-sm" />
              <span>Stoppen</span>
            </button>
            {station.isPaused ? (
              <button
                onClick={handleResumeSession}
                className="bg-blue-500 hover:bg-blue-600 text-white px-3 py-2 rounded-md text-sm font-medium transition-colors flex items-center justify-center space-x-1"
              >
                <SafeIcon icon={FiPlay} className="text-sm" />
                <span>Fortsetzen</span>
              </button>
            ) : (
              <button
                onClick={handlePauseSession}
                className="bg-yellow-500 hover:bg-yellow-600 text-white px-3 py-2 rounded-md text-sm font-medium transition-colors flex items-center justify-center space-x-1"
              >
                <SafeIcon icon={FiPause} className="text-sm" />
                <span>Pausieren</span>
              </button>
            )}
          </div>

          <button
            onClick={handleOpenKioskClick}
            disabled={station.isPaused}
            className={`w-full px-3 py-2 rounded-md text-sm font-medium transition-colors flex items-center justify-center space-x-1 ${
              station.isPaused
                ? 'bg-gray-300 text-gray-500 cursor-not-allowed'
                : 'bg-green-500 hover:bg-green-600 text-white'
            }`}
          >
            <SafeIcon icon={FiShoppingBag} className="text-sm" />
            <span>Bestellen</span>
          </button>

          <button
            onClick={copyMobileUrl}
            className="w-full bg-blue-500 hover:bg-blue-600 text-white px-3 py-2 rounded-md text-sm transition-colors flex items-center justify-center space-x-1"
            title="Mobile Bestelllink kopieren"
          >
            <SafeIcon icon={FiSmartphone} className="text-sm" />
            <span>Mobile Link</span>
          </button>
        </div>
      ) : (
        <div className="space-y-3">
          {showStartForm ? (
            <div className="space-y-3">
              <input
                type="text"
                placeholder="Kundenname eingeben (optional)..."
                value={customerName}
                onChange={(e) => setCustomerName(e.target.value)}
                className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                onKeyPress={(e) => e.key === 'Enter' && handleStartSession()}
                autoFocus
              />
              
              {station.type === 'playstation' && (
                <div className="flex items-center justify-between bg-gray-50 p-2 rounded-md border border-gray-200">
                  <span className="text-sm font-medium text-gray-700">Controller:</span>
                  <div className="flex items-center space-x-3">
                    <button 
                      onClick={() => setControllerCount(Math.max(1, controllerCount - 1))}
                      className="w-8 h-8 rounded-full bg-white border border-gray-300 flex items-center justify-center hover:bg-gray-100 text-gray-600 transition-colors"
                    >
                      <SafeIcon icon={FiMinus} />
                    </button>
                    <span className="font-bold w-4 text-center">{controllerCount}</span>
                    <button 
                      onClick={() => setControllerCount(Math.min(4, controllerCount + 1))}
                      className="w-8 h-8 rounded-full bg-white border border-gray-300 flex items-center justify-center hover:bg-gray-100 text-gray-600 transition-colors"
                    >
                      <SafeIcon icon={FiPlus} />
                    </button>
                  </div>
                </div>
              )}

              <div className="flex space-x-2">
                <button
                  onClick={handleStartSession}
                  className="flex-1 bg-green-500 hover:bg-green-600 text-white px-3 py-2 rounded-md text-sm font-medium transition-colors"
                >
                  Starten
                </button>
                <button
                  onClick={() => {
                    setShowStartForm(false);
                    setCustomerName('');
                    setControllerCount(1);
                  }}
                  className="px-3 py-2 border border-gray-300 rounded-md text-sm hover:bg-gray-50 transition-colors"
                >
                  Abbrechen
                </button>
              </div>
            </div>
          ) : (
            <button
              onClick={() => setShowStartForm(true)}
              className="w-full bg-blue-500 hover:bg-blue-600 text-white px-4 py-2 rounded-md font-medium transition-colors flex items-center justify-center space-x-2"
            >
              <SafeIcon icon={FiPlay} className="text-sm" />
              <span>Session starten</span>
            </button>
          )}
        </div>
      )}
    </div>
  );
}

export default StationCard;